import React, { useCallback, useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { adminAPI } from '../../services/api';
import PageLayout from '../../components/PageLayout';
import DataTable from '../../components/ui/DataTable';
import CustomerDetailDrawer from '../../components/customer/CustomerDetailDrawer';
import { CustomerCreateModal } from '../../components/CustomerModals';
import { CustomerImportModal } from '../../components/CustomerImportModal';
import toast from 'react-hot-toast';
import {
  UserIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  PlusIcon,
  ArrowUpTrayIcon,
} from '@heroicons/react/24/outline';

interface ResyncSummary {
  connectedAccountId: string;
  localCustomersProcessed: number;
  stripeCustomersProcessed: number;
  linkedLocalByStripeId: number;
  linkedLocalByEmail: number;
  createdStripeFromLocal: number;
  createdLocalFromStripe: number;
}

function AdminCustomers() {
  const queryClient = useQueryClient();
  const location = useLocation();
  const navigate = useNavigate();
  const [filters, setFilters] = useState({
    search: '',
    kycStatus: '',
    loyaltyTier: '',
    page: 1,
    limit: 20
  });
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [lastResyncSummary, setLastResyncSummary] = useState<ResyncSummary | null>(null);
  const [lastResyncAt, setLastResyncAt] = useState<Date | null>(null);

  const setPageIntent = useCallback((intent?: 'create' | 'import') => {
    const params = new URLSearchParams(location.search);
    params.delete('create');
    params.delete('import');

    if (intent === 'create') {
      params.set('create', 'customer');
    }
    if (intent === 'import') {
      params.set('import', 'customers');
    }

    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate]);

  const resyncCustomersMutation = useMutation(
    () => adminAPI.resyncCustomers(),
    {
      onSuccess: (response) => {
        const summary = response.data as ResyncSummary;
        setLastResyncSummary(summary);
        setLastResyncAt(new Date());
        toast.success(
          `Resync complete: ${summary.createdStripeFromLocal} created in Stripe, ${summary.createdLocalFromStripe} added locally, ${summary.linkedLocalByEmail + summary.linkedLocalByStripeId} linked.`
        );
        queryClient.invalidateQueries('admin-customers');
      },
      onError: (error: any) => {
        toast.error(error?.response?.data?.message || 'Failed to resync customers');
      },
    }
  );

  const handleResyncCustomers = () => {
    if (resyncCustomersMutation.isLoading) {
      return;
    }

    const confirmed = window.confirm(
      'Run customer resync now? This will link existing records and create any missing local or Stripe customers for this tenant.'
    );
    if (!confirmed) {
      return;
    }

    resyncCustomersMutation.mutate();
  };

  useEffect(() => {
    const state = location.state as { openCustomer?: any } | null;
    const customerFromPalette = state?.openCustomer;

    if (!customerFromPalette) {
      return;
    }

    setSelectedCustomer(customerFromPalette);

    // Clear one-time navigation state so refresh/navigation does not re-open unexpectedly.
    navigate(location.pathname + location.search, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setIsCreateModalOpen(params.get('create') === 'customer');
    setIsImportModalOpen(params.get('import') === 'customers');
  }, [location.search]);

  const { data: customersData, isLoading } = useQuery(
    ['admin-customers', filters],
    () => adminAPI.getCustomers(filters),
    { 
      select: (response) => response.data,
      keepPreviousData: true
    }
  );

  const customers = customersData?.customers || [];
  const pagination = customersData?.pagination;
  const summary = customersData?.summary;
  const customerTableRows = customers.map((customer: any) => ({
    ...customer,
    display_name: [customer.first_name, customer.last_name].filter(Boolean).join(' ') || customer.email,
  }));

  const tableColumns = [
    {
      key: 'display_name',
      header: 'Customer',
      render: (customer: any) => (
        <div className="min-w-0">
          <p className="font-medium text-on-surface truncate">{customer.display_name}</p>
          <p className="text-xs text-on-surface-variant truncate">{customer.email || 'No email'}</p>
        </div>
      ),
    },
    {
      key: 'id',
      header: 'Stripe ID',
      render: (customer: any) => (
        <div>
          <span className="font-mono text-xs text-on-surface-variant">{customer.id}</span>
          {!!customer.historical_stripe_customer_ids?.length && (
            <p className="mt-1 text-xs text-on-surface-variant">{customer.historical_stripe_customer_ids.length} historical billing profile{customer.historical_stripe_customer_ids.length === 1 ? '' : 's'}</p>
          )}
          {customer.identity_reconciliation_required && <p className="mt-1 text-xs text-error">Identity review required</p>}
        </div>
      ),
    },
    {
      key: 'customer_status',
      header: 'Status',
      render: (customer: any) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-pill ${
          customer.customer_status === 'Active'
            ? 'bg-success-container text-success-on-container'
            : 'bg-error-container text-error-on-container'
        }`}>
          {customer.customer_status || 'Unknown'}
        </span>
      ),
    },
    {
      key: 'registrationDate',
      header: 'Created',
      render: (customer: any) => (
        <span>{new Date(customer.registrationDate).toLocaleDateString()}</span>
      ),
    },
    {
      key: 'lastActivity',
      header: 'Last Synced',
      render: (customer: any) => (
        <span>{new Date(customer.lastActivity).toLocaleDateString()}</span>
      ),
    },
    {
      key: 'actions',
      header: 'Actions',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (customer: any) => (
        <button
          type="button"
          className="btn-secondary px-3 py-1.5 text-xs"
          onClick={() => setSelectedCustomer(customer)}
        >
          Open
        </button>
      ),
    },
  ];

  return (
    <PageLayout>
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-on-surface">Stripe Customers</h1>
            <p className="mt-2 text-on-surface-variant">Customers synced directly from your connected Stripe account</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleResyncCustomers}
              disabled={resyncCustomersMutation.isLoading}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <ArrowPathIcon className={`h-5 w-5 ${resyncCustomersMutation.isLoading ? 'animate-spin' : ''}`} />
              {resyncCustomersMutation.isLoading ? 'Resyncing...' : 'Resync Local + Stripe'}
            </button>
            <button
              type="button"
              onClick={() => setPageIntent('import')}
              className="btn-secondary inline-flex items-center gap-2"
            >
              <ArrowUpTrayIcon className="h-5 w-5" />
              Import CSV
            </button>
            <button
              type="button"
              onClick={() => setPageIntent('create')}
              className="btn-primary inline-flex items-center gap-2"
            >
              <PlusIcon className="h-5 w-5" />
              Create Customer
            </button>
          </div>
        </div>
      </div>

      {lastResyncSummary && lastResyncAt && (
        <div className="card p-4 mb-6 border border-blue-100 bg-blue-50 text-blue-900">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold">Last Customer Resync</h3>
              <p className="text-xs mt-1">
                {lastResyncAt.toLocaleString()} on account {lastResyncSummary.connectedAccountId}
              </p>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div>
              <p className="text-xs uppercase tracking-wide">Linked</p>
              <p className="font-semibold">{lastResyncSummary.linkedLocalByStripeId + lastResyncSummary.linkedLocalByEmail}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide">Created in Stripe</p>
              <p className="font-semibold">{lastResyncSummary.createdStripeFromLocal}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide">Created Locally</p>
              <p className="font-semibold">{lastResyncSummary.createdLocalFromStripe}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide">Processed</p>
              <p className="font-semibold">{lastResyncSummary.localCustomersProcessed} local / {lastResyncSummary.stripeCustomersProcessed} Stripe</p>
            </div>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="card p-4">
            <div className="flex items-center">
              <UserIcon className="h-8 w-8 text-blue-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface-variant">Total Customers</p>
                <p className="text-2xl font-bold text-on-surface">{summary.totalCustomers}</p>
              </div>
            </div>
          </div>
          
          {/* <div className="card p-4">
            <div className="flex items-center">
              <CheckCircleIcon className="h-8 w-8 text-green-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface-variant">Verified</p>
                <p className="text-2xl font-bold text-on-surface">{summary.verifiedCustomers}</p>
              </div>
            </div>
          </div> */}
          
          {/* <div className="card p-4">
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-8 w-8 text-purple-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface-variant">Avg LTV</p>
                <p className="text-2xl font-bold text-on-surface">${summary.averageLifetimeValue}</p>
              </div>
            </div>
          </div>
           */}
          {/* <div className="card p-4">
            <div className="flex items-center">
              <XMarkIcon className="h-8 w-8 text-red-600" />
              <div className="ml-3">
                <p className="text-sm font-medium text-on-surface-variant">Churn Rate</p>
                <p className="text-2xl font-bold text-on-surface">{summary.churnRate}%</p>
              </div>
            </div>
          </div> */}
        </div>
      )}

      {!summary && (
        <div className="card p-4 mb-6 flex items-start gap-3 bg-blue-50 border border-blue-100 text-blue-900">
          <ArrowPathIcon className="h-5 w-5 mt-0.5" />
          <p className="text-sm">
            This view now reads customers directly from Stripe. Legacy user-based customer records are not used for this page.
          </p>
        </div>
      )}

      {/* Filters */}
      <div className="card p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="relative">
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Search</label>
            <div className="relative">
              <input
                type="text"
                className="input-field pl-10"
                placeholder="Search customers..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value, page: 1 })}
              />
              <MagnifyingGlassIcon className="h-5 w-5 text-on-surface-variant absolute left-3 top-2.5" />
            </div>
          </div>

          {/* <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">KYC Status</label>
            <select
              className="input-field"
              value={filters.kycStatus}
              onChange={(e) => setFilters({ ...filters, kycStatus: e.target.value, page: 1 })}
            >
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
            </select>
          </div> */}

          {/* <div>
            <label className="block text-sm font-medium text-on-surface-variant mb-1">Loyalty Tier</label>
            <select
              className="input-field"
              value={filters.loyaltyTier}
              onChange={(e) => setFilters({ ...filters, loyaltyTier: e.target.value, page: 1 })}
            >
              <option value="">All Tiers</option>
              <option value="bronze">Bronze</option>
              <option value="silver">Silver</option>
              <option value="gold">Gold</option>
              <option value="platinum">Platinum</option>
            </select>
          </div> */}

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', kycStatus: '', loyaltyTier: '', page: 1, limit: 20 })}
              className="btn-secondary w-full"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <DataTable
          columns={tableColumns}
          data={customerTableRows}
          loading={isLoading}
          onRowClick={(customer) => {
            setSelectedCustomer(customer);
          }}
          emptyState={
            <div className="text-center py-6">
              <UserIcon className="h-10 w-10 text-on-surface-variant mx-auto mb-3" />
              <h3 className="text-base font-medium text-on-surface mb-1">No customers found</h3>
              <p className="text-on-surface-variant">No customers match your current filters.</p>
              <button
                type="button"
                onClick={() => setPageIntent('create')}
                className="btn-primary mt-4 inline-flex items-center gap-2"
              >
                <PlusIcon className="h-5 w-5" />
                Create Customer
              </button>
            </div>
          }
        />

        {pagination && pagination.totalPages > 1 && (
          <div className="bg-surface-container-low px-6 py-3 flex items-center justify-between rounded-card">
            <div className="text-sm text-on-surface-variant">
              Page {filters.page} of {pagination.totalPages} ({pagination.total} total)
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                disabled={filters.page <= 1}
                className="btn-secondary"
              >
                Previous
              </button>
              <button
                onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                disabled={filters.page >= pagination.totalPages}
                className="btn-secondary"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <CustomerDetailDrawer
        open={Boolean(selectedCustomer)}
        onClose={() => setSelectedCustomer(null)}
        customer={selectedCustomer}
        allowEdit
      />

      <CustomerCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setPageIntent()}
      />

      <CustomerImportModal
        isOpen={isImportModalOpen}
        onClose={() => setPageIntent()}
      />

    </PageLayout>
  );
}

export default AdminCustomers;
